import { useState, useEffect } from "react";
import Logic from "./logic";

import TradeEntryType from "../../components/analyser/TradeEntryType";
import WatchlistWildcardNone from "../../components/analyser/WatchlistWildcardNone";
import TradeAnalysis from "../../components/analyser/TradeAnalysis";
import InstrumentAnalytics from "../../components/analyser/InstrumentAnalytics";
import PositionType from "../../components/analyser/PositionType";
import Returns from "../../components/analyser/PercentReturns";
import InstrumentAnalyticsByDate from "../../components/analyser/InstrumentAnalyticsByDate";
import DayAnalyticsByDate from "../../components/analyser/DayAnalyticsByDate";
import Stats from "../../components/analyser/Stats";
import TradingStatisticsPanel from "../../components/analyser/TradingStatisticsPanel";
import UnarchivedTestsDropDown from "../../components/select/unarchivedTestsDropDown";
import useWindowDimensions from "../../utils/width";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import EquityCurve from "../../components/equityCurve";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import DateRangeIcon from "@mui/icons-material/DateRange";
import EventIcon from "@mui/icons-material/Event";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PercentIcon from "@mui/icons-material/Percent";

const AnalyserPage = () => {
  const {
    trades,
    runSpeficCal,
    theme,
    changeYear,
    auth,
    runSpeficCalStats,
    calculateFields,
  } = Logic();
  const { width } = useWindowDimensions();
  const [selectedTab, setSelectedTab] = useState(() => {
    const savedTab = localStorage.getItem("analyser-selected-tab");
    return savedTab !== null ? parseInt(savedTab, 10) : 0;
  });
  const [showGross, setShowGross] = useState(() => localStorage.getItem("show-gross") === "true");
  const [selectedBacktest, setSelectedBacktest] = useState(() => {
    const activeBacktest = localStorage.getItem("backtest-active");
    return activeBacktest && activeBacktest !== "null" ? activeBacktest : "";
  });
  const [selectedTimeframe, setSelectedTimeframe] = useState("Weekly");
  const [showMetric, setShowMetric] = useState(() => localStorage.getItem("showMetric") || "percentage");

  // Trigger data refresh when analyzer page loads to ensure fresh data
  useEffect(() => {
    // Small delay to ensure the component is fully mounted
    setTimeout(() => {
      window.dispatchEvent(new Event("account-changed"));
    }, 100);
  }, []);

  // Listen for gross-net-changed events from dashboard
  useEffect(() => {
    const handleGrossNetChanged = (event) => {
      if (event.detail && event.detail.showGross !== undefined) {
        setShowGross(event.detail.showGross);
      }
    };

    const handleBacktestChanged = () => {
      const activeBacktest = localStorage.getItem("backtest-active");
      const newBacktest = activeBacktest && activeBacktest !== "null" ? activeBacktest : "";
      setSelectedBacktest(newBacktest);
    };

    window.addEventListener("gross-net-changed", handleGrossNetChanged);
    window.addEventListener("backtest-changed", handleBacktestChanged);
    
    return () => {
      window.removeEventListener("gross-net-changed", handleGrossNetChanged);
      window.removeEventListener("backtest-changed", handleBacktestChanged);
    };
  }, []);

    
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    localStorage.setItem("analyser-selected-tab", newValue.toString());
    
    // Scroll down when E-Curve tab is selected
    if (newValue === 1) {
      setTimeout(() => {
        window.scrollTo({
          top: 400,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  const getButtonStyles = () => ({
    padding: width >= 900 ? "6px 12px" : width >= 790 ? "6px 12px" : width >= 500 ? "5px 10px" : "4px 8px",
    fontSize: width >= 790 ? "12px" : width >= 500 ? "12px" : "11px",
    borderRadius: width >= 790 ? "18px" : "16px",
    fontWeight: "600",
    letterSpacing: width >= 500 ? "0.3px" : "0.2px",
    transition: "all 0.2s ease",
    cursor: "pointer",
  });

  
  return (
    trades && (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          margin: "0 auto",
          width: "100%",
          height: "100%",
          marginTop: '-5px',
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
          color: theme[0] === "dark" ? "#FCFCFF" : "black",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            flexDirection: width >= 1430 ? "row" : "column",
            justifyContent: width >= 1430 ? "space-between" : "center",
            margin: "0",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: "calc(100% - 40px)",
              margin: "1.2em 20px 0 20px",
              textAlign: width <= 768 ? "center" : "left",
              display: "flex",
              justifyContent: width <= 768 ? "center" : "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              padding: "0 20px",
              backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#ffffff",
              borderRadius: "12px",
              border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
              paddingTop: "16px",
              paddingBottom: "16px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", justifyContent: "space-between", width: "100%" }}>

            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                padding: "4px",
                borderRadius: "20px",
                backgroundColor: theme[0] === "dark" ? "rgba(45, 46, 51, 0.5)" : "rgba(249, 250, 251, 0.8)",
                border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
              }}
            >
              <div
                onClick={() => handleTabChange(null, 0)}
                style={{
                  ...getButtonStyles(),
                  backgroundColor: selectedTab === 0
                    ? theme[0] === "dark"
                      ? "rgba(59, 130, 246, 0.2)"
                      : "rgba(59, 130, 246, 0.15)"
                    : "transparent",
                  border: selectedTab === 0 ? `2px solid #3b82f6` : "2px solid transparent",
                  color: selectedTab === 0
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <QueryStatsIcon sx={{ fontSize: width >= 790 ? "16px" : width >= 390 ? "14px" : "12px" }} />
                Analytics
              </div>
              <div
                onClick={() => handleTabChange(null, 1)}
                style={{
                  ...getButtonStyles(),
                  backgroundColor: selectedTab === 1
                    ? theme[0] === "dark"
                      ? "rgba(59, 130, 246, 0.2)"
                      : "rgba(59, 130, 246, 0.15)"
                    : "transparent",
                  border: selectedTab === 1 ? `2px solid #3b82f6` : "2px solid transparent",
                  color: selectedTab === 1
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <ShowChartIcon sx={{ fontSize: width >= 790 ? "16px" : width >= 390 ? "14px" : "12px" }} />
                Equity Curve
              </div>
            </div>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {auth.backtestingMode && (
              <Box sx={{ display: "flex", justifyContent: width < 768 ? "center" : "flex-start", width: width < 768 ? "100%" : "auto" }}>
                <UnarchivedTestsDropDown
                  selectedTest={selectedBacktest}
                  onTestSelect={(testId) => {
                  setSelectedBacktest(testId);
                  // Handle the localStorage and event dispatch here
                  if (testId === "Missed Trades") {
                    localStorage.setItem("backtest-active", "Missed Trades");
                  } else if (testId && testId !== "") {
                    localStorage.setItem("backtest-active", testId);
                  } else {
                    localStorage.removeItem("backtest-active");
                  }
                  window.dispatchEvent(new Event("backtest-changed"));
                  // Small delay to ensure state updates propagate
                  setTimeout(() => {
                    window.dispatchEvent(new Event("account-changed"));
                  }, 50);
                }}
                showBacktestTrades={() => {}} // No-op function since we don't need to show trades in analyzer
                label="Select Backtest"
                includeMissedTrades={true}
                variant="compact"
              />
              </Box>
            )}
            {selectedTab === 0 && (
              <>
              {(!auth.backtestingMode || selectedBacktest === "Missed Trades" || !selectedBacktest) && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => changeYear()}
                  sx={{
                    textTransform: "none",
                    fontSize: width >= 500 ? "13px" : "11px",
                    borderRadius: "8px",
                    padding: width >= 500 ? "6px 14px" : "4px 8px",
                    minHeight: width >= 500 ? "32px" : "28px",
                    fontWeight: 500,
                    backgroundColor: theme[0] === "dark" ? "#25262b" : "#ffffff",
                    color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                    border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                    "&:hover": {
                      backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#f3f4f6",
                      borderColor: theme[0] === "dark" ? "#3d3e43" : "#d1d5db",
                    },
                  }}
                >
                  {localStorage.getItem("year") === "0" ? "All" : localStorage.getItem("year")}
                </Button>
              )}
              </>
            )}
            </Box>

            {selectedTab === 1 && (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: width < 768 ? "center" : "flex-start",
                }}
              >
                {["Weekly", "Monthly", "Quarterly", "Yearly"].map((timeframe) => {
                  const isActive = selectedTimeframe === timeframe;
                  const getIcon = () => {
                    switch(timeframe) {
                      case "Weekly": return <CalendarViewWeekIcon sx={{ fontSize: width >= 500 ? "16px" : "14px" }} />;
                      case "Monthly": return <CalendarViewMonthIcon sx={{ fontSize: width >= 500 ? "16px" : "14px" }} />;
                      case "Quarterly": return <DateRangeIcon sx={{ fontSize: width >= 500 ? "16px" : "14px" }} />;
                      case "Yearly": return <EventIcon sx={{ fontSize: width >= 500 ? "16px" : "14px" }} />;
                      default: return null;
                    }
                  };
                  return (
                    <Button
                      key={timeframe}
                      variant="text"
                      size="small"
                      onClick={() => {
                        setSelectedTimeframe(timeframe);
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: width >= 500 ? "13px" : "11px",
                        borderRadius: "8px",
                        padding: width >= 500 ? "6px 14px" : "4px 8px",
                        minHeight: width >= 500 ? "32px" : "28px",
                        fontWeight: 500,
                        backgroundColor: isActive
                          ? (theme[0] === "dark" ? "#3b82f6" : "#2563eb")
                          : "transparent",
                        color: isActive
                          ? "#ffffff"
                          : (theme[0] === "dark" ? "#e5e7eb" : "#374151"),
                        border: isActive
                          ? "1px solid #3b82f6"
                          : (theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb"),
                        "&:hover": {
                          backgroundColor: isActive
                            ? "#2563eb"
                            : (theme[0] === "dark" ? "#2d2e33" : "#f3f4f6"),
                          borderColor: isActive
                            ? "#2563eb"
                            : (theme[0] === "dark" ? "#3d3e43" : "#d1d5db"),
                        },
                      }}
                      startIcon={getIcon()}
                    >
                      {timeframe}
                    </Button>
                  );
                })}
                <>
                  {width > 768 && (
                    <div style={{
                      width: "1px",
                      height: "24px",
                      backgroundColor: theme[0] === "dark" ? "#3d3e43" : "#d1d5db",
                      margin: "0 4px"
                    }} />
                  )}
                  <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    const newMetric = showMetric === "equity" ? "percentage" : "equity";
                    setShowMetric(newMetric);
                    localStorage.setItem("showMetric", newMetric);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: width >= 500 ? "13px" : "11px",
                    borderRadius: "8px",
                    padding: width >= 500 ? "6px 14px" : "4px 8px",
                    minHeight: width >= 500 ? "32px" : "28px",
                    fontWeight: 500,
                    backgroundColor: "transparent",
                    color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                    border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                    "&:hover": {
                      backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#f3f4f6",
                      borderColor: theme[0] === "dark" ? "#3d3e43" : "#d1d5db",
                    },
                  }}
                  startIcon={showMetric === "equity" ? <AccountBalanceIcon sx={{ fontSize: width >= 500 ? "16px" : "14px" }} /> : <PercentIcon sx={{ fontSize: width >= 500 ? "16px" : "14px" }} />}
                >
                  {showMetric === "equity" ? "Balance" : "Percent"}
                </Button>
                  {(!auth.backtestingMode || selectedBacktest === "Missed Trades" || !selectedBacktest) && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => changeYear()}
                      sx={{
                        textTransform: "none",
                        fontSize: width >= 500 ? "13px" : "11px",
                        borderRadius: "8px",
                        padding: width >= 500 ? "6px 14px" : "4px 8px",
                        minHeight: width >= 500 ? "32px" : "28px",
                        fontWeight: 500,
                        backgroundColor: "transparent",
                        color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                        border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                        "&:hover": {
                          backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#f3f4f6",
                          borderColor: theme[0] === "dark" ? "#3d3e43" : "#d1d5db",
                        },
                      }}
                    >
                      {localStorage.getItem("year") === "0" ? "All" : localStorage.getItem("year")}
                    </Button>
                  )}
                </>
              </div>
            )}
            </Box>
          </Box>

          {selectedTab === 0 && (
            <>
          <div style={{ width: "100%", padding: "0 20px 40px 20px" }}>
            <TradingStatisticsPanel trades={trades} theme={theme} showGross={showGross} />
          </div>
          
          <div
            style={{
              width: width >= 1430 ? "50%" : "100%",
              padding: "0 20px 40px 20px",
            }}
          >
            <DayAnalyticsByDate
              trades={trades}
              theme={theme}
              runSpeficCal={runSpeficCal}
              showGross={showGross}
            />
            <Returns
              trades={trades}
              runSpeficCal={runSpeficCal}
              theme={theme}
              showGross={showGross}
            />
            <TradeAnalysis
              trades={trades}
              runSpeficCal={runSpeficCal}
              theme={theme}
              showGross={showGross}
            />

            <TradeEntryType
              trades={trades}
              runSpeficCal={runSpeficCal}
              theme={theme}
              showGross={showGross}
            />

          
            <WatchlistWildcardNone
              trades={trades}
              runSpeficCal={runSpeficCal}
              theme={theme}
              showGross={showGross}
            />
            <PositionType
              trades={trades}
              runSpeficCal={runSpeficCal}
              theme={theme}
              showGross={showGross}
            />
                      </div>
          <div
            style={{
              width: width >= 1430 ? "48%" : "100%",
              padding: "0 20px 40px 20px",
            }}
          >

            <Stats
              trades={trades}
              theme={theme}
              runSpeficCalStats={runSpeficCalStats}
              showGross={showGross}
            />
            
            <InstrumentAnalytics
              trades={trades}
              runSpeficCal={runSpeficCal}
              theme={theme}
              showGross={showGross}
            />
            <InstrumentAnalyticsByDate
              trades={trades}
              runSpeficCal={runSpeficCal}
              theme={theme}
              showGross={showGross}
            />
          </div>
            </>
          )}
          
          {selectedTab === 1 && (
            <div style={{ 
              width: "100%", 
              maxWidth: "1800px",
              margin: "0.5em auto", 
              height: "calc(100vh - 140px)",
              overflow: "hidden",
              padding: "0 20px",
            }}>
              <EquityCurve
                calculateFields={calculateFields}
                trades={trades}
                equityCurve={true}
                setEquityCurve={() => {}}
                selectedTimeframe={selectedTimeframe}
                showMetric={showMetric}
              />
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default AnalyserPage;
